import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

/** Error de la API con el código estable que manda el backend (se traduce en la UI). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown,
  ) {
    super(code);
  }
}

interface ErrorBody {
  error?: { code?: string };
}

const isErrorBody = (value: unknown): value is ErrorBody =>
  typeof value === 'object' && value !== null && 'error' in value;

/** Convierte cualquier respuesta de error en ApiError. */
export function toApiError(status: number, body: unknown): ApiError {
  const code = isErrorBody(body) && typeof body.error?.code === 'string' ? body.error.code : `http_${status}`;
  return new ApiError(status, code, body);
}

const onUnauthorized = new Set<() => void>();
export const subscribeUnauthorized = (listener: () => void) => {
  onUnauthorized.add(listener);
  return () => onUnauthorized.delete(listener);
};

const authMiddleware: Middleware = {
  onResponse({ response, request }) {
    if (response.status === 401 && !request.url.endsWith('/api/me')) onUnauthorized.forEach((fn) => fn());
    return undefined;
  },
};

export const api = createClient<paths>({ baseUrl: '/', credentials: 'same-origin' });
api.use(authMiddleware);

/** Devuelve data o lanza ApiError: para usar dentro de queryFn / mutationFn. */
export async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) throw toApiError(response.status, error);
  return data as T;
}
